/**
 * The 12 biomes of docs/05-content-plan.md §2.
 *
 * A BiomeDef is pure data: worldgen reads it, the renderer reads its parallax
 * and grade, the ecology workstream reads its enemy ids. Nothing here executes.
 *
 * Two authoring rules worth stating, because they are easy to get wrong:
 *
 *  1. `palette.primary` and `palette.shell` MUST be materials that can hold a
 *     wall. Most are RIGID. Meatworks deliberately breaks that (its bulk is
 *     FLESH, a sludge) — worldgen compensates by flagging non-rigid bulk cells
 *     STATIC so the walls do not slump the moment the sim starts. "The walls
 *     are alive" should be a threat, not a landslide.
 *
 *  2. `neighbours` is the cross-contamination graph, not a spatial adjacency.
 *     In Layered mode 15–25% of a region's materials and enemies are drawn
 *     from these — that is what makes a cyberpunk drone in the crypt *mean*
 *     something (docs/05 §1). Keep the graph sparse; every biome bleeding into
 *     every other is the same as no identity at all.
 *
 *  3. **Every biome declares two liquids, and the pair is the biome's lesson**
 *     (docs/03 §5.2a). One liquid per biome meant no liquid could ever meet
 *     another liquid: contamination swaps `secondary`/`fill`/`accent`/`hazard`
 *     and deliberately never touches `liquid`, so `WATER + LAVA` — the single
 *     most important pair in the game — could not occur anywhere, in any seed.
 *     Twelve authored, guaranteed pairs beat a contamination lottery, so the
 *     second liquid is chosen to be the *other half* of that biome's signature
 *     interaction and worldgen pools the two adjacently.
 */

import { MAT, registerMaterials } from '../sim/materialData';
import type { MatId } from '../sim/materials';
import type { RoomArchetype } from './rooms';

/** Traversal abilities gate routes. One name per docs/05 §2 "Traversal demand". */
export type TraversalAbility =
  | 'fluid_adapt'
  | 'breach'
  | 'cling'
  | 'burrow'
  | 'airstep'
  | 'tether'
  | 'blink'
  | 'phase_drift'
  | 'updraft';

export interface BiomePalette {
  /**
   * Bulk terrain.
   *
   * **It must clear a 20/255 luminance step against `secondary`** — issue #79
   * and `tests/paletteSeparation.test.ts`. The Nanite Reef shipped with
   * CIRCUIT_PLATE (92) and PLASTEEL (85), so the two materials making 52 % of
   * that band were one material with two names, in the band that scored the
   * highest `exp(H)` in the game. `exp(H)` counts how many materials share the
   * area and cannot see that two of them are the same colour, which is why the
   * rule has to be stated here and not left to that metric.
   */
  primary: MatId;
  /** Strata banding through the bulk. Twenty from `primary` — see above. */
  secondary: MatId;
  /** Loose deposits that settle on floors. */
  fill: MatId;
  /** Pooled liquid. Placed dry-stacked; the sim settles it on first tick. */
  liquid: MatId;
  /**
   * The biome's *other* liquid — the half that makes `liquid` a puzzle rather
   * than scenery. Worldgen stamps it as a lobe against the primary pool, so
   * the pair is guaranteed to co-occur without being pre-reacted at spawn.
   * See the header note (3) and docs/03 §5.2a.
   */
  liquid2: MatId;
  /** Ambient hazard material — usually a gas, sometimes a hot solid. */
  hazard: MatId;
  /**
   * Ore / signature deposit pockets.
   *
   * None of these may equal `primary` or `secondary`. A vein of the bulk rock
   * is a vein nobody can see, and since worldgen now draws one accent per
   * `MATERIAL_SPAN` cells over a `ORE_WAVELENGTH`-cell field, it is a *large*
   * thing nobody can see. `tests/materialGrain.test.ts` asserts it.
   *
   * **Not being the same material is not enough** — issue #79. Eight accent
   * slots across six biomes declared a *distinct* material within 20/255 of
   * their own bulk in luminance, two of them within 2, which is the identical
   * defect #60 fixed for `shell` one slot down. The step is 20 rather than the
   * shell's 40 because a vein only has to read against the rock it touches,
   * where a shell has to outline a room from across it.
   * `tests/paletteSeparation.test.ts` asserts that, prints every pair's margin,
   * and states in its header which adjacencies it deliberately does not score.
   */
  accents: MatId[];
  /**
   * Structural SHELL material. High hardness: it bounds rooms.
   *
   * **It must also be visible against the rock.** Eight of the twelve biomes
   * shipped with `shell` identical to `primary` or `secondary` and three more
   * within 15/255 of one of them in luminance, so in two thirds of the game the
   * room walls were made of the material of the rock they were cut into: the
   * rooms were generated and could not be seen (`docs/17` §1.5 cause 5,
   * measured as face purity 0.494 in `docs/21` §2.3).
   *
   * The contract, asserted in `tests/materialGrain.test.ts`:
   *
   *   - not `primary`, not `secondary`;
   *   - at least a **40/255 luminance step** from both — `docs/21` §3.3(b);
   *   - at least as hard as `primary`, or the wall is the cheapest way through.
   *
   * The roster makes the first two harder than they look: it holds exactly one
   * bright, opaque, non-flammable RIGID material (CERAMIC, 203) — GLASS is
   * `alpha: 0.35`, BONE is hardness 3 and burns, ICE melts, and everything else
   * above 150 emits. So three biomes whose bulk is dark share it, and the
   * contrast, not the material, is what carries the identity.
   */
  shell: MatId;
  /**
   * One-way floor material.
   *
   * The semi-solid subsystem (materials.ts `oneWaySolid`, the `M.solid`
   * derivation, `body.ts` collision, `world.isOneWay`) shipped with no material
   * in the world for it to act on: the only reference to either material
   * outside its own definition was a test file. Ledges are where a platform
   * belongs, so rooms.ts stamps `Kind.PLATFORM` for the ledges that used to be
   * solid SHELL and worldgen resolves it through this slot.
   *
   * **THIS SLOT IS AUTHORED FOR CONTRAST, NOT FOR MATERIAL FAMILY** — issue
   * #114, `docs/24` §6, and it is the one slot in the palette where legibility
   * outranks fiction. The old rule was "`CATWALK` where the biome is built of
   * organics, `IRON_GRATE` where it is built of metal", and it produced seven
   * biomes whose one-way floor sat inside #79's 20/255 step of their own rock,
   * worst `cathedral_of_salt` at **5/255**.
   *
   * It is not only a legibility defect. A one-way floor is the one material in
   * the roster whose *rule* differs from everything around it — you stand on
   * it, jump up through it, drop off it — so a platform drawn the value of the
   * wall is a traversal affordance the player cannot see. That is a sharper
   * complaint than the one #114 makes, and it is why this slot gets the
   * stricter of the two rules the project has:
   *
   *   - a **20/255 step** against both bulk slots, as an accent must clear
   *     (#79); **and**
   *   - a **different 40/255 luminance bin** from both of them.
   *
   * The second clause is not a restatement of the first. 40/255 is the
   * project's standing definition of "different" and the bin is the unit
   * `docs/24`'s `collapse` ratio is computed in, so a platform that clears 20
   * and lands in the bulk's own bin is still, measurably, the same colour as
   * the wall: `coldforge` CATWALK 126 against PERMAFROST 147 clears the step by
   * 7 and is in bin 3 with it, and that arrangement scored the roster's *worst*
   * collapse figure at 1.76. A full 40/255 step would be the tidier rule and it
   * is not reachable — the twelve bulk pairs leave gaps that need **five**
   * distinct one-way materials to fill, or six biomes sharing one.
   *
   * `tests/paletteStructure.test.ts` asserts both clauses and prints every
   * biome's gap and bins. It was watched red against all twelve.
   */
  platform: MatId;
  /**
   * The biome's own light — issue #31.
   *
   * MUST be a material with `emissive >= LIGHT_SOURCE_MIN`, or the biome has no
   * light source anywhere in it and renders every cell at `ambientLight`, which
   * is the defect #31 opens with. `tests/lighting.test.ts` asserts that.
   *
   * This is an *identity* slot, not a lighting knob: bioluminescent glowcap in
   * the rot, an ember seam in the foundry, void-glow in the Choir. Two biomes
   * at the same ambient with different glow materials are two different places,
   * and that is the whole reason #31 recommends authored emitters over simply
   * raising the global floor — a torch in a 0.8-ambient room is not a torch.
   */
  glow: MatId;
}

export interface BiomeTerrain {
  /** 0..1 — fraction of volume carved to open space. */
  caveDensity: number;
  /** 0..1 — bias toward shafts and vertical rooms over galleries. */
  verticality: number;
  /** 0..1 — small chambers .. large halls. Scales room interiors. */
  roomSizeBias: number;
  /** fbm octaves for strata/ore detail. */
  octaves: number;
  /** Cells per noise unit for strata/ore. Larger = broader banding. */
  noiseScale: number;
  /** 0..1 — how much of the open volume gets liquid pools. */
  liquidLevel: number;
  /** 0..1 — loose deposit frequency on floors. */
  depositDensity: number;
  /** 0..1 — hazard gas pocket frequency. */
  hazardDensity: number;
  /**
   * 0..1 — the share of bulk terrain a biome's accent veins take.
   *
   * Read by `worldgen.ts` `oreThreshold`, which turns it into a threshold on
   * the ore field by inverting that field's measured CDF, so the number means
   * what it says: 0.10 is a tenth of the rock, 0.36 is a third of it.
   *
   * It was declared on all twelve biomes and read by **nothing** until #60 —
   * one of eight authored fields in this repo found with no consumer. The vein
   * share was a hardcoded 0.62 threshold, i.e. **21% of the bulk in every biome
   * alike**, which is also why `docs/17` §1.3 could find no authored knob that
   * predicted the outcome.
   *
   * The twelve values now bracket that 21% instead of every biome sitting on
   * it — 0.10 in the quiet silt of the Sunken Vault, 0.36 in the Long Hour,
   * whose bulk is crystal and whose veins are what the crystal grew through —
   * and they keep the ranking the roster was authored with. Total vein area
   * across the roster is deliberately close to what the constant produced: the
   * point of the field is *which biome* is veined, not that there is less ore.
   *
   * `tests/materialGrain.test.ts` asserts both that something reads it and that
   * perturbing it changes the world.
   */
  oreDensity: number;
  /**
   * 0..1 — probability that a 24×24-cell span of exposed wall carries a vein of
   * `palette.glow` (worldgen.ts `glowVeins`).
   *
   * Deliberately *inverse* to `ambientLight` across the spine. A bright band
   * does not need its walls to carry it, and a dark one is only playable
   * because they do — so the deeper you go the more of the light is local,
   * directional and destructible. That is the progression pressure #31 is
   * after: the same torch is worth more at the bottom.
   */
  glowDensity: number;
}

/** Three parallax depths, 0xRRGGBB. Renderer owns the art; this is the key. */
export interface BiomeParallax {
  far: number;
  mid: number;
  near: number;
}

/** Palette-grade LUT parameters. Cheap because terrain colour is a palette. */
export interface BiomeGrade {
  tint: number;
  saturation: number;
  contrast: number;
  exposure: number;
}

export interface BiomeHazard {
  id: string;
  label: string;
  /** The material the ambient system emits. */
  material: MatId;
  /** 0..1 severity. Drives emission rate and damage scaling. */
  intensity: number;
  desc: string;
}

export interface BiomeDef {
  id: string;
  name: string;
  era: string;
  /** Canonical descent order. `depthBias` walks this list as you go down. */
  index: number;

  palette: BiomePalette;
  terrain: BiomeTerrain;
  ambientTemp: number;
  /**
   * Light floor for this biome — issue #31, option 3.
   *
   * The multiplier the fragment shader applies to a material's palette colour
   * where the light field contributes nothing (`render/terrain.ts`:
   * `col * uGrade * (uAmbient + incident * uLightGain)`). `world/lighting.ts`
   * samples these as control points on a continuous curve in world y rather
   * than reading them per band, because a step in ambient at a band line reads
   * as a rendering bug.
   *
   * Falls down the spine, and that is the point. A single global constant could
   * be "the surface at noon" or "the bottom of the Hollow Choir" and not both,
   * and picking one made the other unplayable — which is how 0.35 shipped.
   */
  ambientLight: number;
  ambientHazard: BiomeHazard;
  parallax: BiomeParallax;
  grade: BiomeGrade;

  /** Ids only — the ecology workstream owns the entities themselves. */
  enemies: string[];
  elites: string[];
  boss: string;

  /** What the biome demands you already have to route through it cleanly. */
  traversal: TraversalAbility[];

  /** Cross-contamination sources (biome ids). */
  neighbours: string[];

  /** The three archetypes that carry this biome's identity. */
  rooms: RoomArchetype[];
}

/**
 * The main path, top to bottom — docs/05 §1 "Descent order", issue #1 phase 2.
 *
 * Depth is progression, so the spine is an *ordered* list of vertical bands and
 * not a roster the generator shuffles. Eight bands at ~900 cells each is the
 * 20-30 minute run the design settled on, which leaves four of the twelve
 * biomes off the spine — see SIDE_CONTENT.
 *
 * The ordering rule is one traversal demand per band, in the order the ability
 * economy hands them out. Read down the list and the `traversal` field of each
 * entry is distinct: cling, fluid_adapt, breach, burrow, airstep, tether, blink,
 * phase_drift. A spine that asked for `breach` twice would be a band you can
 * skip the lesson of.
 *
 * MAIN_PATH[0] *used to be* the surface as well: everything above y = 0 wore
 * its palette, "so descending out of the trees puts you deeper into the same
 * place". docs/24 §10 measured what that convenience cost — the one authored
 * landmark on the surface drawn in exactly the two materials of the ground it
 * stands on — and the surface now declares its own palette
 * (`SURFACE_PALETTE` below). The continuity survives where it was real: the
 * grove's trees and the pond's margin still share the Rotwood's organic
 * accents, and band 0 begins at the ground line rather than above it.
 */
export const MAIN_PATH: readonly string[] = [
  'rotwood_reach',
  'sunken_vault',
  'emberworks',
  'cinder_march',
  'cathedral_of_salt',
  'verdigris_spire',
  'nanite_reef',
  'hollow_choir',
];

/**
 * The four biomes that are side content rather than spine, in the order they
 * flank the bands (two bands each).
 *
 * Each was cut for the same reason: it duplicates a traversal demand the spine
 * already teaches, so as a band it would be a checkpoint you walk through
 * rather than a lesson.
 *
 *  - `chlorine_fen`   — a second `fluid_adapt` after the Sunken Vault, and its
 *                       CONCRETE bulk collides with the Cinder March's.
 *  - `meatworks`      — `breach` + `cling`, both already spent by band 2.
 *  - `coldforge`      — a third `breach`.
 *  - `the_long_hour`  — `updraft`, the one ability the spine deliberately does
 *                       not require; as a wing it is a discovery instead.
 *
 * They are not lesser content — they are what the horizontal extent is *for*.
 * Two runs differ in which wings you found, not only in their seed.
 */
export const SIDE_CONTENT: readonly string[] = [
  'chlorine_fen',
  'meatworks',
  'coldforge',
  'the_long_hour',
];

/**
 * The surface's own palette — docs/24 §10, docs/27 §8.1 and §12 item 4.
 *
 * The surface wore band 0's palette as a convenience, and the convenience put
 * the Great Tree (`WOOD` / `PETRIFIED_WOOD`) on ground made of exactly those
 * two materials: §2's invisible-wall defect at the scale of a landmark, with
 * only the silhouette carrying it. The prologue is the counter-example this
 * declaration copies — declare your own materials, borrow nothing — and it is
 * the one place in the game that reads unambiguously.
 *
 * **Single-bulk on purpose.** `primary === secondary`, so `hostRockOf`'s
 * strata field has nothing to choose between and the ground is one material
 * with texture inside it — which is the *reference* state: the prologue
 * measures region dominance 99.3 % and it is what the whole of docs/24 §11/§12
 * moves the underground toward. A surface is one place; two rocks would be a
 * quilt with a tree on it.
 *
 * **Both landmarks constrain the bulk, and between them they rule out every
 * pale stone.** The tree owns `WOOD`/`PETRIFIED_WOOD`; the mountain owns
 * `GRANITE`/`LIMESTONE` for its bulk and rind and `SANDSTONE`/`SAND` for the
 * desert — `tests/mountain.test.ts` asserts the ground shares none of them,
 * and the first draft of this palette (`LIMESTONE` bulk) was caught by
 * exactly that assertion, which is the guard doing its job. What is left that
 * reads as ground is the dark end of the roster.
 *
 * The slots (luminance out of 255 — WOOD 79, PETRIFIED_WOOD 112):
 *
 *   - `CHITIN` bulk (52): 0x492f28 — on screen it is dark humus, whatever the
 *     material table calls it, and a myth-era surface made of what lived
 *     before is the same sentence `BONE` says one slot up. 27 under the
 *     trunk, 60 under the strata, disjoint from both landmarks by identity.
 *   - `BONE` crust (205): the pale line the player walks on, 153 over the
 *     bulk — nearly four times `shell`'s 40-step rule. NOTE: softer than a
 *     room shell wants, deliberately — a crust is not a wall, and turf that
 *     digs easier than rock is the right way round.
 *   - `SHELF_FUNGUS` platform: the grove's trunks and the Tree's rungs keep
 *     their sage green — a rung must never share the trunk's material, or the
 *     ladder §13.1 builds its whole control on disappears into it.
 *   - `GRAVEL` accents (120): ore pockets on the surface are **buried till**
 *     — plain grey stones under the humus, 68 over the bulk. Not `WOOD`,
 *     though buried roots were the first draft: the tree's own material in
 *     the ground is exactly the collision this palette removes, and
 *     `tests/greatTree.test.ts` now says so by identity. Loose on purpose:
 *     `veinShare` halves a non-solid accent and `materialise` paints it only
 *     where rock encloses it, so the pockets stay pockets.
 *   - `WATER` / `MUD`: the pond is already water (`worldgen.pond`); its other
 *     liquid is wet earth rather than the Rotwood's alcohol.
 *
 * Not a `BiomeDef` and not in `MAIN_PATH`/`SIDE_CONTENT`: the surface has no
 * era, no boss and no room archetypes — it is a place the palette machinery
 * passes through, not a band the descent visits. `worldgen.blockPalettes`
 * reads this for every block above y = 0.
 *
 * A function with a cache rather than a `const`, for `buildBiomes`'s reason:
 * `MAT`'s ids are populated by `registerMaterials()`, so a module-level
 * literal would capture `undefined` for every slot.
 */
export function surfacePalette(): BiomePalette {
  if (cachedSurface !== null) return cachedSurface;
  cachedSurface = {
    primary: MAT.CHITIN,
    secondary: MAT.CHITIN,
    fill: MAT.SPORE_DUST,
    liquid: MAT.WATER,
    liquid2: MAT.MUD,
    hazard: MAT.METHANE,
    accents: [MAT.GRAVEL],
    shell: MAT.BONE,
    platform: MAT.SHELF_FUNGUS,
    glow: MAT.GLOWCAP,
  };
  return cachedSurface;
}

let cachedSurface: BiomePalette | null = null;

let cached: BiomeDef[] | null = null;

/**
 * Build the roster. Idempotent and cached — worldgen, UI and tests all call it,
 * and BiomeDefs are read-only data so sharing instances is correct and cheap.
 */
export function buildBiomes(): BiomeDef[] {
  if (cached !== null) return cached;
  // MAT ids only exist after registration; calling it here means no caller has
  // to remember an ordering constraint.
  registerMaterials();
  const M = MAT;

  const defs: BiomeDef[] = [
    {
      id: 'sunken_vault',
      name: 'The Sunken Vault',
      era: 'ancient',
      index: 0,
      palette: {
        // Was LIMESTONE (131) with SANDSTONE (140) as an accent — a vein
        // **9/255** from the rock it was drawn into, which is issue #79's
        // defect and #60's `shell` defect one slot over. The repair is to swap
        // the two rather than to evict the sandstone: the vault is *cut into*
        // sandstone, `SANDSTONE.shatteredInto` is SAND, and `acid_eats_stone`
        // (CORROSIVE x SANDSTONE -> SAND) becomes a bulk-scale interaction
        // instead of a note about a minor accent. LIMESTONE keeps its home one
        // band down, in the Cathedral, which is the biome it was named for.
        primary: M.SANDSTONE,
        secondary: M.CORPSE_STONE,
        fill: M.GRAVE_SOIL,
        // Water + oil: the oil floats (density 880 < 1000) and turns the
        // surface of every flooded gallery into a fuse — docs/03 §5.2.
        liquid: M.WATER,
        liquid2: M.OIL,
        hazard: M.STEAM,
        // SAND is what the vault's own rock shatters into — 31 from the
        // sandstone bulk and 99 from the corpse stone.
        accents: [M.SAND, M.BONE, M.SALT, M.GLOWCAP],
        // GRANITE (87) sat 15/255 from CORPSE_STONE (72) — a shell the same
        // value as the strata it bands through. Glazed funerary CERAMIC (203)
        // is 63 from the sandstone and 131 from the corpse stone.
        shell: M.CERAMIC,
        // CATWALK (126) sat 15/255 from SANDSTONE (140) and in its bin, and
        // IRON_GRATE (70) is 2/255 from CORPSE_STONE — the vault is the one
        // biome in the roster that neither existing one-way floor can be seen
        // in (#114, docs/24 §6). Bracket fungus at 172 is 32 from the
        // sandstone, 100 from the corpse stone and alone in bin 4; it is also
        // what a flooded tomb grows, and this biome already draws GLOWCAP.
        platform: M.SHELF_FUNGUS,
        glow: M.GLOWCAP,
      },
      terrain: {
        caveDensity: 0.46, verticality: 0.25, roomSizeBias: 0.55,
        octaves: 4, noiseScale: 42, liquidLevel: 0.55,
        depositDensity: 0.1, hazardDensity: 0.02, oreDensity: 0.10, glowDensity: 0.62,
      },
      ambientTemp: 12,
      ambientLight: 0.6,
      ambientHazard: {
        id: 'tidal_flood', label: 'Tidal Flooding', material: M.WATER,
        intensity: 0.6,
        desc: 'Water rises on a slow cycle. Low galleries drown; high ones become the route.',
      },
      parallax: { far: 0x141d26, mid: 0x1d2c36, near: 0x2b3f46 },
      grade: { tint: 0x6f9ec4, saturation: 0.78, contrast: 1.06, exposure: 0.9 },
      enemies: ['drowned_acolyte', 'silt_crawler', 'vault_eel', 'tomb_barnacle', 'cistern_shade', 'flood_shaper'],
      elites: ['brine_warden', 'ossuary_tide'],
      boss: 'the_first_drowned',
      traversal: ['fluid_adapt'],
      neighbours: ['cathedral_of_salt', 'chlorine_fen', 'rotwood_reach'],
      rooms: ['arena', 'corridor', 'cache'],
    },
    {
      id: 'emberworks',
      name: 'Emberworks',
      era: 'early industry',
      index: 1,
      palette: {
        primary: M.RUSTED_IRON,
        secondary: M.CONCRETE,
        fill: M.COAL_DUST,
        // A foundry: the quench trough and the oil that leaks into it. Water
        // on the MOLTEN_IRON veins flash-solidifies them back to iron
        // (docs/03 §5.2); oil on the same water does not put anything out.
        liquid: M.MACHINE_OIL,
        liquid2: M.WATER,
        hazard: M.SMOKE,
        // STEEL (145) was **2/255** from CONCRETE (147): the foundry's
        // signature vein and the foundry's own slab, the same colour twice.
        // #79. What a foundry actually has in quantity that is not iron is the
        // green casting sand its moulds are rammed in — SAND (171), 84 from
        // the rusted iron and 24 from the concrete. STEEL loses nothing by
        // leaving: it is still what `nanite_reef` and `meatworks` are shelled
        // in, and MOLTEN_IRON is still the pour.
        //
        // IRON (107) went with it, and it is the slot #79's table missed:
        // against RUSTED_IRON (87) it measures **19.98**, i.e. it fails the
        // same rule by four hundredths. "The metal that did not rust" is good
        // fiction and it is not a visible vein, which is the only thing this
        // slot is for. CERAMIC (203) — the firebrick a cupola and its flues are
        // lined with, and the one thing in the works that is not metal — is 116
        // from the rusted iron and 56 from the concrete. The foundry's iron is
        // not an ore here anyway: it is the wall.
        accents: [M.CERAMIC, M.SAND, M.MOLTEN_IRON, M.EMBER_VEIN],
        // STEEL (145) against CONCRETE (147) was a 2/255 step — the same
        // colour, twice. A foundry's refractory lining is the black slag glass
        // it pours: OBSIDIAN (47), 40 from the rusted iron and 100 from the
        // concrete, and hardness 8 against the bulk's 3.
        shell: M.OBSIDIAN,
        // IRON_GRATE (70) was 17/255 from RUSTED_IRON (87) and in bin 2 with
        // it: a dark grate on dark rusted plate. Galvanised mesh (184) is 97
        // from the rust and 37 from the concrete, and the zinc is the reason
        // it is the one walkway in the works the rust has not taken.
        platform: M.ZINC_GRATE,
        glow: M.EMBER_VEIN,
      },
      terrain: {
        caveDensity: 0.42, verticality: 0.5, roomSizeBias: 0.6,
        octaves: 4, noiseScale: 34, liquidLevel: 0.35,
        depositDensity: 0.22, hazardDensity: 0.05, oreDensity: 0.16, glowDensity: 0.68,
      },
      ambientTemp: 58,
      ambientLight: 0.55,
      ambientHazard: {
        id: 'dust_suspension', label: 'Coal-Dust Suspension', material: M.COAL_DUST,
        intensity: 0.75,
        desc: 'Disturbed dust hangs in the air. It does not burn — it detonates.',
      },
      parallax: { far: 0x1a1512, mid: 0x2b201a, near: 0x403024 },
      grade: { tint: 0xd08a44, saturation: 0.92, contrast: 1.12, exposure: 1.0 },
      enemies: ['bellows_hound', 'slag_walker', 'rivet_swarm', 'furnace_tender', 'pipe_lurker', 'stokefire_shaper'],
      elites: ['overseer_engine', 'blast_foreman'],
      boss: 'the_great_bellows',
      traversal: ['breach'],
      neighbours: ['cinder_march', 'coldforge', 'verdigris_spire'],
      rooms: ['corridor', 'shaft', 'vault'],
    },
    {
      id: 'rotwood_reach',
      name: 'Rotwood Reach',
      era: 'myth',
      index: 2,
      palette: {
        primary: M.WOOD,
        secondary: M.PETRIFIED_WOOD,
        fill: M.SPORE_DUST,
        // Fungal mat and spirits. Alcohol ignites at 90 °C and burns almost
        // invisibly (docs/03 §5.2) — in a wood full of spore clouds that is
        // the most dangerous liquid in the game to be careless with.
        liquid: M.FUNGAL_MAT,
        liquid2: M.ALCOHOL,
        hazard: M.SPORE_CLOUD,
        // Still four accents, and three of them can now be seen. #73: this
        // biome authored `FUNGAL_MAT`, `MUD`, `METHANE`, `GLOWCAP`, of which
        // **one was solid** — the worst ratio in the roster — and two were
        // invisible on top of that. Both facts are measurable:
        //
        //   - `worldgen.ts` `veinShare` halves a **non-solid** accent and
        //     `materialise` paints it only where the rock encloses it. Both
        //     halves are correct and load-bearing, and together they mean a
        //     non-solid accent is a vein budget the biome cannot spend: 0.14
        //     declared realised as **8.9 %** of the bulk, against
        //     sunken_vault's 14.5 % on a *smaller* declared 0.10.
        //   - `FUNGAL_MAT` is **1/255** from PETRIFIED_WOOD in luminance and
        //     `MUD` is 13 from WOOD — veins of the rock they are drawn into,
        //     which is the defect #60 fixed for `shell`, one slot over.
        //
        // Band 0 came out at `exp(H)` 4.75 against a floor of 5, second worst
        // in the game. `FUNGAL_MAT` loses nothing by leaving this slot: it is
        // still the biome's `liquid`, where it reads as a pool rather than as a
        // sealed pocket of the wall's own colour. What replaces the two is what
        // a rotting wood has lying in it — beetle husks and carcasses, 52 and
        // 205 against a bulk of 79/112, and both solid.
        //
        // METHANE stays non-solid on purpose: a sealed gas pocket is the point
        // of it — breach one and the room fills with the other half of
        // `methane_detonation`.
        // CHITIN (52) left in #114 for the reason one slot down did: it clears
        // #79's 20/255 step against WOOD (79) by 7 and sits in **bin 1 with
        // it**, so the husks were drawn the wood's own value. SPORE_DUST (144)
        // is 65 from the wood, 32 from the petrified wood and alone in bin 3 —
        // and it is this biome's own `fill`, so a seam of packed spore in the
        // rot is the coarse end of a fiction the Reach already tells.
        accents: [M.METHANE, M.SPORE_DUST, M.BONE, M.GLOWCAP],
        // Was PETRIFIED_WOOD, which is also `secondary`: the rooms were cut
        // into the rock and then painted with it. Fired-clay shrine brick
        // (CERAMIC, 203) is 124 from the wood and 91 from the petrified wood —
        // and against a bulk of hardness 2 it is the only thing here that
        // reads as built rather than grown.
        shell: M.CERAMIC,
        // CATWALK (126) was 14/255 from PETRIFIED_WOOD (112) — a wooden
        // walkway in a wood, which is exactly the fiction-first rule #114
        // found had cost seven biomes their one-way floor. Bracket fungus
        // grows in tiers off rotting timber, is 93 from the wood and 60 from
        // the petrified wood, and puts the floor in a bin nothing else here
        // occupies.
        platform: M.SHELF_FUNGUS,
        glow: M.GLOWCAP,
      },
      terrain: {
        caveDensity: 0.5, verticality: 0.62, roomSizeBias: 0.5,
        octaves: 5, noiseScale: 38, liquidLevel: 0.28,
        // oreDensity 0.14 -> 0.22: see the accents above and §2b of
        // `tests/materialGrain.test.ts`. Three quarters of the 0.14 was drawn
        // as a non-solid accent, halved and then sealed, so what the roster
        // read as a modest vein budget realised as almost none. 0.22 over
        // three solid accents realises 22.9 % of the bulk, which is where a
        // 0.22 belongs, and it is the ordering test one slot over that says so.
        depositDensity: 0.26, hazardDensity: 0.14, oreDensity: 0.16, glowDensity: 0.55,
      },
      ambientTemp: 18,
      ambientLight: 0.66,
      ambientHazard: {
        id: 'spore_bloom', label: 'Spore Bloom', material: M.SPORE_CLOUD,
        intensity: 0.7,
        desc: 'Blooms fill the canopy with fuel-air mixture. A torch is a decision.',
      },
      parallax: { far: 0x121a12, mid: 0x1d2a18, near: 0x2e3d20 },
      grade: { tint: 0x8fc05a, saturation: 0.88, contrast: 1.0, exposure: 0.94 },
      enemies: ['fungal_beast', 'bark_stalker', 'sporeling_swarm', 'mycel_weaver', 'rot_crown', 'bloom_shaper'],
      elites: ['heartwood_colossus', 'the_mycelial_choirmaster'],
      boss: 'motherbole',
      traversal: ['cling'],
      neighbours: ['sunken_vault', 'chlorine_fen', 'meatworks'],
      rooms: ['shaft', 'arena', 'cache'],
    },
    {
      id: 'cinder_march',
      name: 'The Cinder March',
      era: 'post-apoc',
      index: 3,
      palette: {
        primary: M.CONCRETE,
        secondary: M.OBSIDIAN,
        fill: M.ASH,
        // Lava and water. The march's own bulk strata are OBSIDIAN, so the
        // most important pair in the game (docs/03 §5.2) literally manufactures
        // the rock around you — which is the clearest way to teach it.
        liquid: M.LAVA,
        liquid2: M.WATER,
        hazard: M.ASH,
        // Three of this biome's four accents were drawn against OBSIDIAN (47)
        // and could not be seen: SOOT **4/255**, CRUDE_TAR **3**, EMBER_VEIN
        // **18** — #79, and the two the issue names plus one it missed.
        //
        // EMBER_VEIN is not replaced, it is simply no longer an *ore*: it is
        // still the march's `glow`, and `glowVeins` draws it on exposed wall
        // where you find it by its light. An emissive material has no business
        // being scored as albedo, and having the ore pass draw it as well was
        // the ore pass competing with the light pass for the same seams.
        //
        // What replaces the other two is what a burnt city leaves. BONE_ASH
        // (180) is 33 from the concrete and 133 from the obsidian — the ash of
        // the column that marched before you, and the biome's fill is already
        // ASH, so this is the coarse end of the same fiction. TAR (21) is the
        // crude tar after it cooled: 126 from the concrete, 26 from the
        // obsidian, and it burns for 1200 ticks against CRUDE_TAR's 900. The
        // two are a phase pair (`TAR.meltsInto CRUDE_TAR` at 45 °C), so in a
        // biome at 92 °C ambient with LAVA for a liquid the crude tar is still
        // very much in play — it is what the accent becomes when the march
        // catches up with it.
        accents: [M.EMBER_GRIT, M.BONE_ASH, M.TAR],
        // OBSIDIAN was `secondary` as well as `shell`, so 28.5% of the band's
        // non-void cells were "room wall" material and none of it bounded a
        // room. The lava/water chain that manufactures obsidian is unchanged —
        // it is still the bulk. What bounds the rooms is what the march is
        // walking over: GRANITE (87), the pre-war foundations, 60 below the
        // concrete it is set into.
        shell: M.GRANITE,
        // IRON_GRATE (70) cleared OBSIDIAN (47) by 23 and shared its bin.
        // The march walks over pre-war infrastructure and galvanised grating
        // is what pre-war infrastructure is made of: 184, 37 from the
        // concrete and 138 from the obsidian.
        platform: M.ZINC_GRATE,
        glow: M.EMBER_VEIN,
      },
      terrain: {
        caveDensity: 0.54, verticality: 0.2, roomSizeBias: 0.7,
        octaves: 4, noiseScale: 48, liquidLevel: 0.2,
        depositDensity: 0.4, hazardDensity: 0.08, oreDensity: 0.12, glowDensity: 0.72,
      },
      ambientTemp: 92,
      ambientLight: 0.51,
      ambientHazard: {
        id: 'ashfall', label: 'Ash-Fall', material: M.ASH,
        intensity: 0.8,
        desc: 'Ash accumulates and buries routes. The map you drew an hour ago is wrong.',
      },
      parallax: { far: 0x1c1918, mid: 0x2a2523, near: 0x3a332f },
      grade: { tint: 0xb08268, saturation: 0.62, contrast: 1.08, exposure: 0.86 },
      enemies: ['cinder_hound', 'ash_wader', 'ember_moth_swarm', 'slagback', 'march_pilgrim', 'drift_shaper'],
      elites: ['the_long_burn', 'cauterised'],
      boss: 'what_walked_out_of_the_fire',
      traversal: ['burrow'],
      neighbours: ['emberworks', 'hollow_choir', 'cathedral_of_salt'],
      rooms: ['arena', 'corridor', 'cache'],
    },
    {
      id: 'cathedral_of_salt',
      name: 'Cathedral of Salt',
      era: 'occult',
      index: 4,
      palette: {
        primary: M.LIMESTONE,
        secondary: M.BONE,
        fill: M.SALT,
        // Ichor and plague. Sanctified mist neutralises the slurry into SALT
        // (docs/03 §5.2) — the material the cathedral is made of — and the
        // ichor cleanses whatever the mist misses.
        liquid: M.RADIANT_ICHOR,
        liquid2: M.PLAGUE_SLURRY,
        hazard: M.SANCTIFIED_MIST,
        // BONE dropped: it was `secondary` as well, so a "vein" of it was the
        // bulk rock at a different threshold — a blob with nothing inside it.
        // SALT (228) was in **bin 5 with BONE (205)** — 23 apart, clearing #79
        // and invisible to the eye's own 40/255 families, which is why this
        // biome scored the roster's worst `collapse` (docs/24 §6). BONE_ASH
        // (180) is 49 from the limestone, 25 from the bone and alone in bin 4:
        // the ash of the burned dead, in an ossuary. SALT stays the `fill` and
        // stays the biome's name.
        accents: [M.RADIANT_MATTER, M.BONE_ASH, M.SAINTLIGHT],
        // CERAMIC (203) against BONE (205) was the palest possible pair of
        // near-identical values. The cathedral's own dead are the contrast it
        // has: CORPSE_STONE (72), 59 below the limestone and 132 below the
        // bone — and it is the material the Sunken Vault above bands with, so
        // the ossuary reads as continuous with the tombs it grew out of.
        shell: M.CORPSE_STONE,
        // CATWALK (126) against LIMESTONE (131) was **5/255**, the worst
        // platform collision in the game and the direct cause of this
        // biome's worst-in-roster collapse figure (docs/24 §6). Dark iron
        // grating is 61 from the limestone and 135 from the bone, and a
        // wrought choir screen is the most cathedral thing in the roster.
        platform: M.IRON_GRATE,
        glow: M.SAINTLIGHT,
      },
      terrain: {
        caveDensity: 0.4, verticality: 0.68, roomSizeBias: 0.85,
        octaves: 3, noiseScale: 56, liquidLevel: 0.14,
        depositDensity: 0.2, hazardDensity: 0.1, oreDensity: 0.12, glowDensity: 0.78,
      },
      ambientTemp: 15,
      ambientLight: 0.47,
      ambientHazard: {
        id: 'consecration_ward', label: 'Consecration Wards', material: M.SANCTIFIED_MIST,
        intensity: 0.55,
        desc: 'Wards read your alignment. Carry cursed matter here and the room objects.',
      },
      parallax: { far: 0x1b1a20, mid: 0x2b2a2e, near: 0x413e3c },
      grade: { tint: 0xf0e2b4, saturation: 0.7, contrast: 1.14, exposure: 1.08 },
      enemies: ['salt_censer', 'reliquary_knight', 'choirbone', 'ward_moth', 'pillar_saint', 'sanctum_shaper'],
      elites: ['the_gilded_penitent', 'antiphon'],
      boss: 'the_seated_choir',
      traversal: ['airstep'],
      neighbours: ['sunken_vault', 'cinder_march', 'hollow_choir'],
      rooms: ['shaft', 'arena', 'vault'],
    },
    {
      id: 'chlorine_fen',
      name: 'The Chlorine Fen',
      era: 'biotech',
      index: 5,
      palette: {
        primary: M.CONCRETE,
        secondary: M.CHITIN,
        fill: M.BIO_SLUDGE,
        // Slurry and standing water. Chlorine is heavier than air, sinks into
        // the water and the water becomes acid (docs/03 §5.2) — the fen's
        // namesake hazard is the reagent.
        liquid: M.PLAGUE_SLURRY,
        liquid2: M.WATER,
        hazard: M.CHLORINE,
        // RUST_FLAKE (78) sat in **bin 1 with CHITIN (52)**. What a chlorine
        // fen actually precipitates is salt — CHLORINE over standing water is
        // the biome's own hazard chain — and SALT (228) is 81 from the
        // concrete and 176 from the chitin. RUST_FLAKE is still what every
        // grate in the game shatters into.
        accents: [M.BIO_SLUDGE, M.ACID, M.SALT, M.GLOWCAP],
        // Was CONCRETE, which is `primary`: every vat in the fen was walled in
        // the same slab it was cut out of. IRON (107) is 41 below the concrete
        // and 55 above the chitin, and it is what the biome's RUST_FLAKE
        // accent is flaking off.
        shell: M.IRON,
        // IRON_GRATE (70) was 18/255 from CHITIN (52). The fen grows over
        // its own vats: bracket fungus at 172 is 25 from the concrete, 120
        // from the chitin, and the only thing in bin 4 here.
        platform: M.SHELF_FUNGUS,
        glow: M.GLOWCAP,
      },
      terrain: {
        caveDensity: 0.52, verticality: 0.22, roomSizeBias: 0.6,
        octaves: 4, noiseScale: 36, liquidLevel: 0.6,
        depositDensity: 0.24, hazardDensity: 0.22, oreDensity: 0.12, glowDensity: 0.66,
      },
      ambientTemp: 26,
      ambientLight: 0.56,
      ambientHazard: {
        id: 'gas_layer', label: 'Toxic Gas Layers', material: M.CHLORINE,
        intensity: 0.85,
        desc: 'Chlorine is heavier than air. It waits at ankle height in every trench.',
      },
      parallax: { far: 0x121816, mid: 0x1c2620, near: 0x2b382c },
      grade: { tint: 0x9fc26a, saturation: 0.84, contrast: 0.98, exposure: 0.88 },
      enemies: ['culture_hound', 'fen_wader', 'vat_swarm', 'sump_grafter', 'bloom_tank', 'slurry_shaper'],
      elites: ['the_grafted_matron', 'chlorine_pope'],
      boss: 'vat_nine',
      traversal: ['fluid_adapt'],
      neighbours: ['rotwood_reach', 'sunken_vault', 'meatworks'],
      rooms: ['corridor', 'arena', 'cache'],
    },
    {
      id: 'verdigris_spire',
      name: 'Verdigris Spire',
      era: 'medieval',
      index: 6,
      palette: {
        primary: M.BRASS,
        secondary: M.RUNESTONE,
        fill: M.GRAVEL,
        // Mercury sinks straight through the cistern water (13500 vs 1000) and
        // superconducts anything the brass is carrying (docs/03 §5.2). The
        // puddle you are standing in is the circuit.
        liquid: M.MERCURY,
        liquid2: M.WATER,
        hazard: M.TOXIC_SMOKE,
        // BRASS dropped: it is `primary`, so that vein was the spire itself.
        accents: [M.CRYSTAL, M.MERCURY],
        // Was RUNESTONE, which is `secondary`. OBSIDIAN (47) is 94 below the
        // brass and 61 below the runestone — black masonry under verdigris,
        // which is what a medieval spire is.
        shell: M.OBSIDIAN,
        platform: M.IRON_GRATE,
        glow: M.CRYSTAL,
      },
      terrain: {
        caveDensity: 0.38, verticality: 0.8, roomSizeBias: 0.65,
        octaves: 3, noiseScale: 30, liquidLevel: 0.18,
        depositDensity: 0.14, hazardDensity: 0.06, oreDensity: 0.16, glowDensity: 1,
      },
      ambientTemp: 16,
      ambientLight: 0.43,
      ambientHazard: {
        id: 'relic_constructs', label: 'Relic Constructs', material: M.MERCURY,
        intensity: 0.5,
        desc: 'Clockwork wakes when charge crosses the brass. Mercury seeps from what breaks.',
      },
      parallax: { far: 0x191c1a, mid: 0x27302a, near: 0x3b4a3c },
      grade: { tint: 0x9fbf8a, saturation: 0.86, contrast: 1.04, exposure: 0.96 },
      enemies: ['brass_sentinel', 'rune_moth', 'gearsworn', 'quicksilver_wisp', 'spire_page', 'lattice_shaper'],
      elites: ['the_verdigris_marshal', 'orrery_knight'],
      boss: 'the_hour_hand',
      traversal: ['tether'],
      neighbours: ['emberworks', 'the_long_hour', 'nanite_reef'],
      rooms: ['shaft', 'vault', 'corridor'],
    },
    {
      id: 'coldforge',
      name: 'Coldforge',
      era: 'far future',
      index: 7,
      palette: {
        primary: M.PERMAFROST,
        secondary: M.PLASTEEL,
        fill: M.SNOW,
        // Coolant at −20 °C beside standing water: the water freezes into a
        // bridge you can walk, and heat anywhere near the coolant is a
        // pressure event rather than a puddle.
        liquid: M.REACTOR_COOLANT,
        liquid2: M.WATER,
        hazard: M.FLASH_VAPOR,
        // SALT rather than a third helping of PLASTEEL (already secondary and
        // shell): ICE + SALT → BRINE is the biome's other authored chain.
        //
        // STEEL (145) was **2/255** from PERMAFROST (147) — the same collision
        // Emberworks had, in the other direction, and #79's joint-worst pair.
        // GRAVEL (120) is 27 from the permafrost and 35 from the plasteel, and
        // it is the honest thing to find inside permafrost: the moraine the ice
        // ground up and froze in place, which is the ground the plant was
        // built on rather than any part of the plant.
        // GRAVEL (120) is #79's repair and #114 found it half-done: 27 from
        // PERMAFROST (147), clearing the 20/255 step and landing in **bin 3
        // with it**. The moraine fiction is right and the rock was wrong —
        // OBSIDIAN (47) is the volcanic glass a sheet grinds off the bedrock
        // and freezes in place, 100 from the permafrost and 38 from the
        // plasteel.
        accents: [M.ICE, M.OBSIDIAN, M.SALT, M.SIGNAL_BLOOM],
        // Was PLASTEEL, which is `secondary`. A cryo plant's structural
        // material is its thermal-barrier ceramic: CERAMIC (203) is 57 above
        // the permafrost and 118 above the plasteel, and `corrosionResist 1.0`
        // is exactly why you would line a coolant hall with it.
        shell: M.CERAMIC,
        // IRON_GRATE (70) was 16/255 from PLASTEEL (85). Galvanised mesh is
        // 37 from the permafrost and 99 from the plasteel, and a coolant
        // hall lined in corrosion-proof ceramic would not floor itself in
        // bare iron.
        platform: M.ZINC_GRATE,
        glow: M.SIGNAL_BLOOM,
      },
      terrain: {
        caveDensity: 0.44, verticality: 0.45, roomSizeBias: 0.72,
        octaves: 4, noiseScale: 44, liquidLevel: 0.32,
        depositDensity: 0.3, hazardDensity: 0.12, oreDensity: 0.16, glowDensity: 0.8,
      },
      ambientTemp: -26,
      ambientLight: 0.45,
      ambientHazard: {
        id: 'coolant_flash', label: 'Coolant Flash-Boil', material: M.FLASH_VAPOR,
        intensity: 0.8,
        desc: 'Heat a coolant line and the room becomes a pressure event, not a puddle.',
      },
      parallax: { far: 0x121820, mid: 0x1b2735, near: 0x2a3b4c },
      grade: { tint: 0x8fc8d8, saturation: 0.72, contrast: 1.1, exposure: 1.02 },
      enemies: ['frost_lathe', 'coolant_wraith', 'rime_swarm', 'plasteel_ram', 'cryo_tender', 'freeze_shaper'],
      elites: ['the_quench', 'zero_foreman'],
      boss: 'coldforge_prime',
      traversal: ['breach'],
      // Cathedral rather than Hollow Choir: road salt on frozen ground is a
      // real interaction (ICE + SALT → BRINE), and Coldforge inheriting the
      // void palette by contamination was three of its worst colour collisions.
      neighbours: ['emberworks', 'nanite_reef', 'cathedral_of_salt'],
      rooms: ['corridor', 'vault', 'arena'],
    },
    {
      id: 'nanite_reef',
      name: 'The Nanite Reef',
      era: 'cyberpunk',
      index: 8,
      palette: {
        primary: M.CIRCUIT_PLATE,
        // **The sharpest case in #79, and it is not an accent.** CIRCUIT_PLATE
        // (92) and PLASTEEL (85) were **7/255** apart: the two materials that
        // make 52 % of the visible area of this band were indistinguishable
        // from each other, in the band that scores the highest `exp(H)` in the
        // game. That pair is the cleanest available proof that `exp(H)`
        // measures composition and not legibility — it counts *how many*
        // materials share the area, and is blind to two of them being the same
        // colour. It is why the bulk-to-bulk rule exists at all and is not an
        // accent-only rule.
        //
        // CHITIN (52) is 40 from the circuit plate. It is also the reading the
        // biome's own name and roster were already asking for: a *reef* is
        // laid down, not machined, and `assembler_crab` and `polyp_prime` are
        // two of its six enemies. The swarm lays chitin over the plate the way
        // coral lays lime over rock. PLASTEEL keeps its home in Coldforge.
        secondary: M.CHITIN,
        fill: M.NANITE_DUST,
        // The swarm assimilates; the acid handles what it cannot. Acid on the
        // reef's own STEEL shell is the two-stage rust chain (docs/03 §5.1).
        liquid: M.NANITE_SWARM,
        liquid2: M.ACID,
        hazard: M.NANITE_FOG,
        // BONE is the reef's BIOMASS: without an organic anywhere in reach,
        // `grey_goo` and `nanite_reanimation` — the two rules the whole nanite
        // faction exists for — had no possible input.
        // CIRCUIT_PLATE dropped: it is `primary`, so the reef's signature vein
        // was indistinguishable from the reef.
        accents: [M.NANITE_SWARM, M.BONE, M.SIGNAL_BLOOM],
        // Unchanged through #60 and #79 both, and the only one of the twelve
        // that has needed nothing: STEEL (145) clears 53 against CIRCUIT_PLATE
        // and 93 against the chitin. It is here as the control.
        shell: M.STEEL,
        // IRON_GRATE (70) was 18/255 from CHITIN (52) and in its bin: the
        // reef's floor was the reef. CATWALK (126) is 34 from the circuit
        // plate and 74 from the chitin — salvaged decking the swarm has
        // grown over rather than replaced, which is what a reef does.
        platform: M.CATWALK,
        glow: M.SIGNAL_BLOOM,
      },
      terrain: {
        caveDensity: 0.48, verticality: 0.55, roomSizeBias: 0.58,
        octaves: 5, noiseScale: 26, liquidLevel: 0.3,
        depositDensity: 0.28, hazardDensity: 0.18, oreDensity: 0.12, glowDensity: 0.96,
      },
      ambientTemp: 32,
      ambientLight: 0.42,
      ambientHazard: {
        id: 'replication', label: 'Replicating Terrain', material: M.NANITE_FOG,
        intensity: 0.9,
        desc: 'The reef regrows what you destroyed. Charge is the only thing that stops it.',
      },
      parallax: { far: 0x101418, mid: 0x172230, near: 0x243a44 },
      grade: { tint: 0x6fd0c0, saturation: 0.95, contrast: 1.16, exposure: 1.04 },
      enemies: ['reef_drone', 'assembler_crab', 'grey_swarm', 'signal_ghost', 'plate_grafter', 'reef_shaper'],
      elites: ['the_static_archivist', 'polyp_prime'],
      boss: 'the_reef_that_reads',
      traversal: ['blink'],
      neighbours: ['coldforge', 'verdigris_spire', 'the_long_hour'],
      rooms: ['corridor', 'shaft', 'vault'],
    },
    {
      id: 'hollow_choir',
      name: 'Hollow Choir',
      era: 'cosmic',
      index: 9,
      palette: {
        primary: M.VOIDGLASS,
        secondary: M.OBSIDIAN,
        fill: M.GRAVEL,
        // Liquid void and radiant ichor — mutual annihilation (docs/03 §5.2),
        // and the only bright thing in a biome graded to 0.45 saturation and
        // 0.7 exposure. The pair is the puzzle and the light source at once.
        liquid: M.LIQUID_VOID,
        liquid2: M.RADIANT_ICHOR,
        hazard: M.VOID_MIST,
        // BONE and GLASS are #73's half of this list. The Choir authored the
        // second-lowest `oreDensity` in the roster over four accents, one of
        // which — `VOID_MATTER` — is a plasma, so `veinShare` halves it and
        // `materialise` seals it, and it is also **8/255** from the VOIDGLASS
        // bulk it is drawn into: a vein nobody can see, which is the defect #60
        // fixed for `shell`, one slot over. The result was that `primary` +
        // `secondary` took **71.6 %** of the visible area and `exp(H)` fell to
        // 4.04, the lowest in the game and the number #73 is about. The
        // choristers and their vitrines are 205 and 199 against a bulk of
        // 18/47, and both are solid.
        // ...and #79 finished the sentence #73 started. `VOID_MATTER` (10) is
        // **8/255** from the VOIDGLASS (18) it is drawn into: after 1b6e45d it
        // was no longer a quarter of the vein budget, but a sixth of a vein
        // budget spent on a vein nobody can see is still spent on nothing. It
        // leaves the ore slot and stays in the biome the way it arrived —
        // VOIDGLASS `shatteredInto` is VOID_MATTER, so every wall you break
        // here still makes it, which is a better account of "what the place is"
        // than a seam of it in the wall was.
        accents: [M.CRYSTAL, M.BRASS, M.GLASS, M.BONE, M.CHOIRLIGHT],
        // The worst case in the roster: shell == primary == VOIDGLASS (18),
        // the material whose own description is "absorbs light entirely", in
        // the band that scored 0 of 18 windows as "reads as a place". The
        // Choir's staves are RUNESTONE (108) — 90 above the voidglass, 61
        // above the obsidian, hardness 7 to match the bulk, and an emissive of
        // 0.18 that stays under LIGHT_SOURCE_MIN so it edges the rooms without
        // becoming a second light source.
        shell: M.RUNESTONE,
        // IRON_GRATE (70) cleared OBSIDIAN (47) by 23 and shared bin 1 with
        // it: in the band a vision judge scored 0 of 18 for "reads as a
        // place", the floors were drawn the strata's own value. CATWALK (126)
        // is 108 from the voidglass and 79 from the obsidian — the one warm
        // thing in the Choir, and the last of what the pilgrims carried down.
        platform: M.CATWALK,
        glow: M.CHOIRLIGHT,
      },
      terrain: {
        caveDensity: 0.56, verticality: 0.7, roomSizeBias: 0.9,
        octaves: 3, noiseScale: 62, liquidLevel: 0.16,
        // oreDensity 0.12 -> 0.24. Deliberately not the 0.30 that clears the
        // floor on this number alone (measured: 5.47): the Hollow Choir is the
        // emptiest place in the game, and making it the second most heavily
        // veined would fix a statistic by contradicting the biome. The two
        // added accents carry the rest — §2b of `tests/materialGrain.test.ts`
        // measures the split between the two levers.
        depositDensity: 0.08, hazardDensity: 0.26, oreDensity: 0.20, glowDensity: 0.92,
      },
      ambientTemp: -42,
      ambientLight: 0.34,
      ambientHazard: {
        id: 'erasure', label: 'Light Absorption', material: M.VOID_MIST,
        intensity: 0.95,
        desc: 'Voidglass eats your light. What the mist touches stops having been there.',
      },
      parallax: { far: 0x0a0810, mid: 0x120e1c, near: 0x1c1630 },
      grade: { tint: 0x6a5aa0, saturation: 0.45, contrast: 1.24, exposure: 0.7 },
      enemies: ['choir_mote', 'null_pilgrim', 'erasure_swarm', 'lamplighter', 'hollow_cantor', 'unmaking_shaper'],
      elites: ['the_absent_note', 'penumbra'],
      boss: 'the_hollow_that_sings',
      traversal: ['phase_drift'],
      neighbours: ['cathedral_of_salt', 'the_long_hour', 'cinder_march'],
      rooms: ['shaft', 'arena', 'vault'],
    },
    {
      id: 'the_long_hour',
      name: 'The Long Hour',
      era: 'temporal',
      index: 10,
      palette: {
        primary: M.CRYSTAL,
        secondary: M.RUNESTONE,
        fill: M.ALCHEMICAL_DUST,
        // VOID × TEMPORAL is an unstable rift (docs/03 §5.1) and the Long Hour
        // is the only place both halves belong. Against a palette of crystal
        // and glass, a near-black liquid is also the biome's best contrast.
        liquid: M.CHRONO_FLUID,
        liquid2: M.LIQUID_VOID,
        hazard: M.TEMPORAL_RESIDUE,
        // CRYSTAL dropped: it is `primary` *and* `glow`, so two of the three
        // accents were the hour's own bulk.
        // GLASS (199) was 28 from CRYSTAL (171) and in **bin 4 with it** — the
        // hour's two largest accents drawn the bulk's own value, in the biome
        // with the roster's largest vein tier. OBSIDIAN (47) is 124 from the
        // crystal: the seams that did not survive, against the ones that did,
        // and the same family as the VOIDGLASS the rift walls the rooms with.
        accents: [M.OBSIDIAN, M.ALCHEMICAL_DUST],
        // Was RUNESTONE, which is `secondary`. VOIDGLASS (18) against CRYSTAL
        // (171) is the largest step in the roster, 153 — and the biome already
        // declares LIQUID_VOID as its second liquid on the grounds that VOID x
        // TEMPORAL belongs here and nowhere else. The rift walls the rooms.
        shell: M.VOIDGLASS,
        platform: M.IRON_GRATE,
        glow: M.CRYSTAL,
      },
      terrain: {
        caveDensity: 0.43, verticality: 0.75, roomSizeBias: 0.68,
        octaves: 5, noiseScale: 22, liquidLevel: 0.24,
        depositDensity: 0.16, hazardDensity: 0.2, oreDensity: 0.08, glowDensity: 0.84,
      },
      ambientTemp: 8,
      ambientLight: 0.41,
      ambientHazard: {
        id: 'echo', label: 'Echo Hazards', material: M.TEMPORAL_RESIDUE,
        intensity: 0.85,
        desc: 'Your own attacks arrive again, a moment late, from where you used to be.',
      },
      parallax: { far: 0x101a1e, mid: 0x1a2a30, near: 0x2b4048 },
      grade: { tint: 0xa8e0f0, saturation: 0.8, contrast: 1.1, exposure: 1.06 },
      enemies: ['hour_moth', 'rewound_knight', 'echo_swarm', 'lag_stalker', 'minute_hand', 'chrono_shaper'],
      elites: ['the_second_pass', 'the_long_minute'],
      boss: 'noon',
      traversal: ['updraft'],
      neighbours: ['verdigris_spire', 'hollow_choir', 'nanite_reef'],
      rooms: ['shaft', 'vault', 'arena'],
    },
    {
      id: 'meatworks',
      name: 'Meatworks',
      era: 'flesh horror',
      index: 11,
      palette: {
        primary: M.FLESH,
        secondary: M.CHITIN,
        fill: M.ROTTEN_FLESH,
        // Blood and digestive acid. CORROSIVE × ORGANIC is the fastest rule in
        // the table, and this is the one biome whose walls are organic — the
        // acid eats the room, not just what is standing in it.
        liquid: M.BLOOD,
        liquid2: M.ACID,
        hazard: M.LIVING_RESIN,
        // CORPSE_STONE (72) was #79's named on-the-line pair — exactly 20.0
        // from CHITIN (52) — and #114 says the rest of it: 20 apart is one
        // 40/255 bin, so the bones of what the works ate were drawn the
        // carapace's own value. BONE_ASH (180) is 80 from the flesh, 128 from
        // the chitin and alone in bin 4. A works renders what it cannot eat;
        // bone meal is the honest product and BONE is still the whole bones.
        accents: [M.LIVING_RESIN, M.BONE, M.BONE_ASH, M.GLOWCAP],
        // Was CHITIN, which is `secondary`: a flesh biome whose walls were the
        // same carapace as its rock had no edge anywhere. STEEL (145) is 45
        // above the flesh and 93 above the chitin — the works, as opposed to
        // the meat, and the one hard thing in a biome of hardness 4.
        shell: M.STEEL,
        // CATWALK (126) cleared FLESH (100) by 26 and landed in bin 3 with the
        // STEEL shell, and the works is the industrial half of this biome's own
        // name: an abattoir floors itself in galvanised mesh so it can be
        // hosed through. ZINC_GRATE (184) is 84 from the flesh, 132 from the
        // chitin, and the swap is worth 0.05 of `collapse` on its own.
        platform: M.ZINC_GRATE,
        glow: M.GLOWCAP,
      },
      terrain: {
        caveDensity: 0.5, verticality: 0.4, roomSizeBias: 0.5,
        octaves: 5, noiseScale: 20, liquidLevel: 0.38,
        depositDensity: 0.3, hazardDensity: 0.16, oreDensity: 0.16, glowDensity: 0.7,
      },
      ambientTemp: 37,
      ambientLight: 0.5,
      ambientHazard: {
        id: 'peristalsis', label: 'The Walls Are Alive', material: M.LIVING_RESIN,
        intensity: 0.9,
        desc: 'Corridors constrict. Resin closes wounds in the terrain you just opened.',
      },
      parallax: { far: 0x1a1010, mid: 0x2a1616, near: 0x3e2020 },
      grade: { tint: 0xc06060, saturation: 0.9, contrast: 1.02, exposure: 0.92 },
      enemies: ['gullet_hound', 'marrow_stalker', 'tick_swarm', 'sinew_anchor', 'graft_parasite', 'peristalsis_shaper'],
      elites: ['the_marrow_judge', 'second_stomach'],
      boss: 'the_thing_that_kept_eating',
      traversal: ['breach', 'cling'],
      neighbours: ['chlorine_fen', 'rotwood_reach', 'sunken_vault'],
      rooms: ['arena', 'corridor', 'cache'],
    },
  ];

  cached = defs;
  return defs;
}

/** Lookup by id. Linear over 12 entries — never called in a hot loop. */
export function biomeById(id: string): BiomeDef | undefined {
  return buildBiomes().find((b) => b.id === id);
}

/** Test-only: drop the cache so a fresh material registry can be picked up. */
export function _resetBiomes(): void {
  cached = null;
}
